import { inject, Injectable, signal } from '@angular/core'
import { Router } from '@angular/router'
import { finalize, from, Observable, of, tap } from 'rxjs'
import { StorageService } from './storage.service'
import { PermissionService } from '@/app/shared/services/permission.service'
import { LoginApiService } from '@/app/shared/services/api/login/login.api.service'

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly #storageService = inject(StorageService)
  readonly #router = inject(Router)
  readonly permissionService = inject(PermissionService)
  readonly loginApiService = inject(LoginApiService)

  readonly #isAuthenticated = signal<boolean>(false)
  readonly #isLoading = signal<boolean>(false)

  public readonly isAuthenticated = this.#isAuthenticated.asReadonly()
  public readonly isLoading = this.#isLoading.asReadonly()

  constructor() {
    this.initializeAuth()
  }

  login(credentials: { email: string; password: string }) {
    this.#isLoading.set(true)

    return from(
      this.loginApiService.login(credentials),
    ).pipe(
      tap((response) => {
        if (response?.token) {
          this.#storageService.setToken(response.token)
          this.#isAuthenticated.set(true)
          this.permissionService.loadCurrentUser()
        }
      }),
      finalize(() => {
        this.#isLoading.set(false)
      }),
    )
  }

  logout(): void {
    this.#storageService.clear()
    this.#isAuthenticated.set(false)
    this.permissionService.clearUser()
    this.#router.navigate(['/login'])
  }

  refreshToken(): Observable<boolean> {
    const token = this.#storageService.getToken()

    if (!token) {
      return of(false)
    }

    if (this.#storageService.isTokenExpired(token)) {
      this.logout()
      return of(false)
    }

    return of(true)
  }

  private initializeAuth(): void {
    const token = this.#storageService.getToken()

    if (token && !this.#storageService.isTokenExpired(token)) {
      this.#isAuthenticated.set(true)
    } else if (token) {
      this.logout()
    }
  }
}
